import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const STATUS_COLOR = { verde: "#10B981", amarillo: "#F59E0B", rojo: "#EF4444", sin_evaluar: "#A1A1AA" };

export default function MapPage() {
    const { user } = useAuth();
    const [data, setData] = useState({ clients: [], inspections: [] });
    useEffect(() => { (async () => setData((await api.get("/map/data")).data))(); }, []);

    const center = data.clients.length > 0
        ? [data.clients[0].gps_lat, data.clients[0].gps_lng]
        : [4.711, -74.072];

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8" data-testid="map-page">
            <div className="mb-8 border-b border-zinc-200 pb-6">
                <div className="overline mb-2" style={{ color: "#0F766E" }}>Distribución geográfica</div>
                <h1 className="font-display text-4xl font-black tracking-tighter">Mapa operacional</h1>
                <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
                    Sedes de clientes (marcadores azules) e inspecciones recientes (círculos de color según semáforo).
                </p>
            </div>

            <div className="grid lg:grid-cols-[1fr_280px] gap-6">
                <div className="border border-zinc-200" style={{ height: "560px" }} data-testid="map-container">
                    <MapContainer center={center} zoom={6} style={{ height: "100%", width: "100%" }}>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; OpenStreetMap'
                        />
                        {data.clients.map(c => (
                            <Marker key={c.client_id} position={[c.gps_lat, c.gps_lng]}>
                                <Popup>
                                    <div className="font-bold text-sm">{c.razon_social}</div>
                                    <div className="text-xs text-zinc-600 mt-1">NIT {c.nit}</div>
                                    <div className="text-xs mt-1">{c.direccion}</div>
                                    <a href={`/clients/${c.client_id}`} className="text-xs mt-2 inline-block" style={{ color: "#0F766E" }}>Ver cliente →</a>
                                </Popup>
                            </Marker>
                        ))}
                        {data.inspections.map(i => (
                            <CircleMarker
                                key={i.inspection_id}
                                center={[i.gps_lat, i.gps_lng]}
                                radius={9}
                                pathOptions={{ color: STATUS_COLOR[i.estado_salud] || "#A1A1AA", fillColor: STATUS_COLOR[i.estado_salud] || "#A1A1AA", fillOpacity: 0.85 }}
                            >
                                <Popup>
                                    <div className="font-bold text-sm">{i.client?.razon_social || "—"}</div>
                                    <div className="text-xs text-zinc-600 mt-1 font-mono">{i.inspection_id}</div>
                                    <div className="text-xs mt-1">{i.sistema_revisado}</div>
                                    <div className="text-xs mt-1">Estado: <span className="font-bold uppercase">{i.estado_salud}</span></div>
                                    <a href={`/inspections/${i.inspection_id}`} className="text-xs mt-2 inline-block" style={{ color: "#0F766E" }}>Ver inspección →</a>
                                </Popup>
                            </CircleMarker>
                        ))}
                    </MapContainer>
                </div>
                <div className="space-y-4">
                    <Legend />
                    <div className="border border-zinc-200 p-4">
                        <div className="overline mb-2">Resumen</div>
                        <div className="space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-zinc-600">Clientes con GPS</span><span className="font-semibold font-mono">{data.clients.length}</span></div>
                            <div className="flex justify-between"><span className="text-zinc-600">Inspecciones</span><span className="font-semibold font-mono">{data.inspections.length}</span></div>
                            <div className="flex justify-between"><span className="text-zinc-600">Verdes</span><span className="font-semibold font-mono">{data.inspections.filter(i => i.estado_salud === "verde").length}</span></div>
                            <div className="flex justify-between"><span className="text-zinc-600">Amarillas</span><span className="font-semibold font-mono">{data.inspections.filter(i => i.estado_salud === "amarillo").length}</span></div>
                            <div className="flex justify-between"><span className="text-zinc-600">Rojas</span><span className="font-semibold font-mono" style={{ color: "#EF4444" }}>{data.inspections.filter(i => i.estado_salud === "rojo").length}</span></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Legend() {
    return (
        <div className="border border-zinc-200 p-4" data-testid="map-legend">
            <div className="overline mb-3">Leyenda</div>
            <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2"><span style={{ background: "#10B981", width: 12, height: 12, borderRadius: "50%" }} />Verde · Saludable</div>
                <div className="flex items-center gap-2"><span style={{ background: "#F59E0B", width: 12, height: 12, borderRadius: "50%" }} />Amarillo · Atención</div>
                <div className="flex items-center gap-2"><span style={{ background: "#EF4444", width: 12, height: 12, borderRadius: "50%" }} />Rojo · Crítico</div>
                <div className="flex items-center gap-2"><span style={{ background: "#A1A1AA", width: 12, height: 12, borderRadius: "50%" }} />Sin evaluar</div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-100"><img src="https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png" alt="" style={{ height: 18 }} />Sede de cliente</div>
            </div>
        </div>
    );
}
